import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, RefreshCw, Smartphone, Copy, ExternalLink, Clock, MessageCircle, Send, Mail, Maximize2, X, Tablet, Wifi, WifiOff } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import api from '@/lib/api';
import { kioskAPI } from '@/lib/api';

/**
 * QR signature dialog for in-clinic iPad/phone signing.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - consentId: string  (currently only consents are supported)
 *  - signerType: 'patient' | 'representative'
 *  - signerLabel: string (display label, e.g. patient name)
 *  - onSigned: () => void  (called when signature completes)
 */
export default function QrSignatureDialog({ open, onOpenChange, consentId, signerType = 'patient', signerLabel, onSigned }) {
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null); // { token, sign_url, expires_at, ... }
  const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'signed' | 'expired' | 'error'
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [sendingChannel, setSendingChannel] = useState(null); // 'sms' | 'whatsapp' | null
  const [presenting, setPresenting] = useState(false); // Pantalla compartida / share screen
  const [kioskList, setKioskList] = useState([]);
  const [kioskOpen, setKioskOpen] = useState(false);
  const [pushingKioskId, setPushingKioskId] = useState(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const stopTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const createSession = async () => {
    if (!consentId) return;
    setLoading(true);
    setError(null);
    setStatus('idle');
    try {
      const { data } = await api.post(
        `/consents/${consentId}/qr-signature-session?signer_type=${signerType}`
      );
      setSession(data);
      setStatus('pending');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'No se pudo generar la sesión de firma';
      setError(detail);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // When the dialog opens, create a fresh session.
  useEffect(() => {
    if (open && !session && status !== 'signed') {
      createSession();
    }
    if (!open) {
      stopTimers();
      setSession(null);
      setStatus('idle');
      setError(null);
      setSecondsLeft(null);
      setPresenting(false);
    }
    return stopTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll status + countdown ticker once we have a session.
  useEffect(() => {
    if (!session?.token || status !== 'pending') return;

    const tick = () => {
      try {
        const ms = new Date(session.expires_at).getTime() - Date.now();
        setSecondsLeft(Math.max(0, Math.round(ms / 1000)));
      } catch { /* ignore */ }
    };
    tick();
    tickRef.current = setInterval(tick, 1000);

    const poll = async () => {
      try {
        const { data } = await api.get(`/signature-request/${session.token}`);
        if (data.status === 'signed') {
          stopTimers();
          setStatus('signed');
          setPresenting(false);
          toast.success('Firma recibida correctamente');
          if (onSigned) onSigned();
          // Auto-close after a short success visualization
          setTimeout(() => onOpenChange?.(false), 1800);
        } else if (data.status === 'expired' || data.status === 'cancelled') {
          stopTimers();
          setStatus('expired');
          setPresenting(false);
        }
      } catch {
        // Soft-fail: keep polling
      }
    };
    pollRef.current = setInterval(poll, 2500);
    return stopTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, status]);

  const copyLink = () => {
    if (!session?.sign_url) return;
    navigator.clipboard.writeText(session.sign_url).then(
      () => toast.success('Enlace copiado'),
      () => toast.error('No se pudo copiar')
    );
  };

  // Load paired kiosks when the dialog opens & we have a session
  useEffect(() => {
    if (!open || status !== 'pending') return;
    let cancelled = false;
    kioskAPI.list().then(({ data }) => {
      if (!cancelled) setKioskList(data?.devices || []);
    }).catch(() => { if (!cancelled) setKioskList([]); });
    return () => { cancelled = true; };
  }, [open, status]);

  const pushToKiosk = async (kiosk) => {
    if (!session?.sign_url || !kiosk?.id) return;
    setPushingKioskId(kiosk.id);
    try {
      const { data } = await kioskAPI.push(kiosk.id, {
        sign_url: session.sign_url,
        signer_label: signerLabel || null,
        document_title: session?.document_title || null,
        expires_at: session?.expires_at || null,
      });
      // After the polling fallback was added, the push always returns 200
      // and queues the event in MongoDB; the iPad picks it up via SSE
      // (instant) or via the 3-second poll (fallback when proxies eat
      // the SSE stream). delivered_to lets us tell the operator whether
      // SSE went through.
      if (data?.delivered_to > 0) {
        toast.success(`Enviado a "${kiosk.name}"`);
      } else {
        toast.success(`Enviado a "${kiosk.name}". La tablet lo recibirá en unos segundos.`, { duration: 6000 });
      }
      setKioskOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar a la tablet');
    } finally {
      setPushingKioskId(null);
    }
  };

  const sendLink = async (channel) => {
    if (!session?.token || !consentId) return;
    setSendingChannel(channel);
    try {
      const { data } = await api.post(
        `/consents/${consentId}/send-signature-link`,
        { token: session.token, channel }
      );
      const r = data?.results || {};
      if (channel === 'sms') {
        if (r.sms === 'sent') toast.success(`SMS enviado al ****${data.phone_last4 || ''}`);
        else toast.error('No se pudo enviar el SMS');
      } else if (channel === 'whatsapp') {
        if (r.whatsapp === 'sent') toast.success(`WhatsApp enviado al ****${data.phone_last4 || ''}`);
        else toast.error('No se pudo enviar el WhatsApp (¿fuera de ventana 24h?)');
      } else if (channel === 'email') {
        if (r.email === 'sent') toast.success(`Email enviado a ${data.email_masked || 'paciente'}`);
        else toast.error('No se pudo enviar el email');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'No se pudo enviar el enlace';
      toast.error(detail);
    } finally {
      setSendingChannel(null);
    }
  };

  const formatTime = (s) => {
    if (s == null) return '';
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  // ESC exits Pantalla Compartida (does NOT close the dialog).
  useEffect(() => {
    if (!presenting) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setPresenting(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [presenting]);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] overflow-hidden" data-testid="qr-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="truncate">Firma con QR (iPad / móvil)</span>
          </DialogTitle>
          <DialogDescription>
            {signerLabel
              ? <>El paciente <strong>{signerLabel}</strong> escanea el QR y firma desde su dispositivo.</>
              : 'El paciente escanea el QR desde un iPad o móvil para firmar.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2 w-full min-w-0">
          {loading || status === 'idle' ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin mb-3" />
              <p className="text-sm">Generando enlace seguro…</p>
            </div>
          ) : status === 'error' ? (
            <div className="h-64 flex flex-col items-center justify-center text-center px-4">
              <p className="text-sm text-red-600 font-medium mb-3">{error}</p>
              <Button variant="outline" onClick={createSession} data-testid="qr-retry">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Reintentar
              </Button>
            </div>
          ) : status === 'signed' ? (
            <div className="h-64 flex flex-col items-center justify-center text-emerald-600 text-center">
              <CheckCircle2 className="w-16 h-16 mb-3" />
              <p className="font-semibold text-lg">¡Firmado!</p>
              <p className="text-sm text-slate-600 mt-1">El documento se ha actualizado.</p>
            </div>
          ) : status === 'expired' ? (
            <div className="h-64 flex flex-col items-center justify-center text-center">
              <Clock className="w-12 h-12 text-brand-deep mb-2" />
              <p className="text-sm text-slate-700 font-medium mb-3">La sesión ha expirado o fue cancelada.</p>
              <Button variant="outline" onClick={createSession} data-testid="qr-restart">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Generar nueva
              </Button>
            </div>
          ) : (
            // pending
            <>
              <div className="bg-white p-3 rounded-lg border-2 border-emerald-100 flex items-center justify-center" data-testid="qr-canvas-wrapper">
                <QRCodeCanvas value={session.sign_url} size={220} level="M" includeMargin={false} />
              </div>
              <div className="flex items-center justify-center flex-wrap gap-2 text-xs text-slate-500 text-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                <span>Esperando firma del dispositivo…</span>
                {secondsLeft != null && (
                  <span className="px-1.5 py-0.5 rounded bg-brand-soft/40 text-brand-deep font-mono">
                    expira en {formatTime(secondsLeft)}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-center flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  onClick={() => setPresenting(true)}
                  data-testid="qr-presenting-mode"
                >
                  <Maximize2 className="w-3.5 h-3.5 mr-1.5" />
                  Pantalla compartida
                </Button>
                {kioskList.length > 0 && (
                  <Popover open={kioskOpen} onOpenChange={setKioskOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        data-testid="qr-send-to-tablet"
                      >
                        <Tablet className="w-3.5 h-3.5 mr-1.5" />
                        Enviar a tablet
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="center">
                      <p className="text-xs font-medium text-slate-600 px-2 pt-1 pb-2">
                        Elige una tablet emparejada
                      </p>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {kioskList.map((k) => (
                          <button
                            key={k.id}
                            type="button"
                            onClick={() => pushToKiosk(k)}
                            disabled={pushingKioskId === k.id || !k.online}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                            data-testid={`push-to-kiosk-${k.id}`}
                          >
                            <Smartphone className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{k.name}</p>
                              {k.doctor_name && (
                                <p className="text-[11px] text-slate-400 truncate">{k.doctor_name}</p>
                              )}
                            </div>
                            {pushingKioskId === k.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                            ) : k.online ? (
                              <Wifi className="w-3.5 h-3.5 text-emerald-500" />
                            ) : (
                              <WifiOff className="w-3.5 h-3.5 text-slate-300" title="Desconectada" />
                            )}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 px-2 pt-2 leading-snug">
                        Solo se puede enviar a tablets conectadas. Si no aparece la tuya,
                        abre la pantalla del iPad.
                      </p>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <div className="w-full min-w-0 flex flex-col gap-1.5">
                <p className="text-xs text-slate-500">Enlace para abrir manualmente en el iPad:</p>
                <div className="flex items-center gap-1.5 w-full min-w-0">
                  <code className="flex-1 min-w-0 truncate text-xs bg-slate-50 border rounded px-2 py-1.5 font-mono">
                    {session.sign_url}
                  </code>
                  <Button size="icon" variant="ghost" className="h-8 w-8 flex-shrink-0" onClick={copyLink} title="Copiar enlace" data-testid="qr-copy-link">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={() => window.open(session.sign_url, '_blank')}
                    title="Abrir en nueva pestaña"
                    data-testid="qr-open-link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={() => sendLink('whatsapp')}
                    disabled={!!sendingChannel}
                    data-testid="qr-send-whatsapp"
                  >
                    {sendingChannel === 'whatsapp'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <MessageCircle className="w-3.5 h-3.5 mr-1.5" />}
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-brand-navy border-brand-medium hover:bg-brand-soft/30"
                    onClick={() => sendLink('sms')}
                    disabled={!!sendingChannel}
                    data-testid="qr-send-sms"
                  >
                    {sendingChannel === 'sms'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    SMS
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-brand-deep border-brand-deep/40 hover:bg-brand-deep/10"
                    onClick={() => sendLink('email')}
                    disabled={!!sendingChannel}
                    data-testid="qr-send-email"
                  >
                    {sendingChannel === 'email'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                    Email
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 leading-snug">
                  Enviar el enlace al teléfono o email registrado del paciente.
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Pantalla compartida — fullscreen distraction-free QR (portaled to body) */}
    {presenting && session?.sign_url && status === 'pending' && typeof document !== 'undefined' && createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-white flex flex-col"
        data-testid="qr-presenting-overlay"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Smartphone className="w-4 h-4" />
            <span>Modo presentación · pulse <kbd className="px-1.5 py-0.5 border rounded text-[11px]">Esc</kbd> para salir</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPresenting(false)}
            data-testid="qr-presenting-exit"
          >
            <X className="w-4 h-4 mr-1.5" /> Cerrar
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          {signerLabel && (
            <p className="text-2xl md:text-3xl text-slate-700 mb-2">
              <span className="font-semibold">{signerLabel}</span>
            </p>
          )}
          <p className="text-base md:text-lg text-slate-500 mb-8">
            Escanee este código con la cámara de su móvil para firmar
          </p>
          <div className="bg-white p-6 rounded-2xl border-4 border-emerald-100 shadow-xl">
            <QRCodeCanvas value={session.sign_url} size={520} level="M" includeMargin={false} />
          </div>
          <div className="mt-8 flex items-center gap-3 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-base">Esperando firma…</span>
            {secondsLeft != null && (
              <span className="px-2 py-1 rounded bg-brand-soft/40 text-brand-deep font-mono text-sm">
                expira en {formatTime(secondsLeft)}
              </span>
            )}
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
